import React, { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from './AuthContext';
import { useToast } from '../contexts/ToastContext';
import AuthModal from './AuthModal';
import SessionGuard from './auth/SessionGuard';
import AppShell from './app/AppShell';
import CommandPalette from './shared/CommandPalette';
import Spinner from './shared/Spinner';
// Eager: shell chrome + overlays + the transient build view + right-panel
// content. These are small and/or must paint instantly, so they stay in the
// main bundle. Everything route-level is lazy-loaded below.
import ThinkingView from './app/builder/ThinkingView';
import LowCreditBanner from './credits/LowCreditBanner';
import OnboardingFlow from './onboarding/OnboardingFlow';
import RecentEpisodesFeed from './builder-v2/RecentEpisodesFeed';
import EpisodeReadingPane from './editor/EpisodeReadingPane';
import EpisodeRelatedPanel from './editor/EpisodeRelatedPanel';
import CommentsSection from './editor/CommentsSection';
import NotificationsPanel from './workspace/NotificationsPanel';
import HomeActivityPanel from './home/HomeActivityPanel';
import { pathFor, viewFromPath, normalizeLegacyHash } from '../lib/routing';
import { unreadCount, seedIfEmpty, countUnreadPitches } from '../lib/notifications';
import { getIncomingRequests } from '../lib/guestRequests';
import { fetchRemixIdea } from '../lib/remix';
import { saveEpisode, saveShortFormScript, getEpisodes, getShortFormScripts, getGuestUses, incGuestUses, FREE_LIMIT, signOut, supabase, updateProfile, deleteEpisode, renameEpisode, markEpisodePublic } from '../lib/supabase';
import { autoSaveDraft, markConsumed } from '../lib/drafts';
import { setPendingEpisode, getPendingEpisode, clearPendingEpisode } from '../lib/pendingEpisodeStore';
import { ensureEpisodesWorkspace } from '../lib/defaultWorkspaces';
import { getWorkspaces } from '../lib/workspaces';
import { getShows, ensureDefaultShow } from '../lib/shows';
import { deductForAction, renewCreditsIfDue, isInsufficientCreditsError, getUserCredits } from '../lib/credits';
import { usePlayer } from './player/PlayerContext';
import { getEpisodeAudioUrl } from '../lib/episodeAudio';
import { openCreatorByUsername } from '../lib/openCreator';
import { episodeBuildCreditAction } from '../lib/modelConfig';
import { recordSuccessfulBuild } from '../lib/retentionStats';
import { summarizeCredits, summarizeShowUsage, effectivePlan } from '../lib/planLimits';
import { addXP, updateStreak, checkMilestones } from '../lib/gamification';
import { generateEpisodeBrief, normalizeQuality } from '../lib/episodeBrief';
import { isHardcodedAdminEmail, recordTopic } from '../lib/memory';

// ── Route-level pages — code-split ──────────────────────────────────────────
// Each view ships in its own chunk instead of one ~1.7MB bundle, so the
// initial load only downloads the shell + the first view. Rendered
// one-at-a-time under <Suspense fallback={<Spinner/>}> (see render).
const LandingV2          = lazy(() => import('./landing-v2/LandingV2'));
const CreateView         = lazy(() => import('./landing-v2/CreateView'));
const EpisodePreviewPage = lazy(() => import('./landing-v2/EpisodePreviewPage'));
const ResultsWorkspace   = lazy(() => import('./results/ResultsWorkspace'));
const importDashboard    = () => import('./app/DashboardView');
const DashboardView      = lazy(importDashboard);
const LibraryPage        = lazy(() => import('../pages/LibraryPage'));
const AnalyticsPage      = lazy(() => import('../pages/AnalyticsPage'));
const BillingPage        = lazy(() => import('../pages/BillingPage'));
const BillingSettingsPage = lazy(() => import('../pages/BillingSettingsPage'));
const SubscriptionPage   = lazy(() => import('../pages/SubscriptionPage'));
const PricingPage        = lazy(() => import('../pages/PricingPage'));
const PartnersPage       = lazy(() => import('../pages/PartnersPage'));
const SettingsPage       = lazy(() => import('../pages/SettingsPage'));
const HelpPage           = lazy(() => import('../pages/HelpPage'));
const SupportPage        = lazy(() => import('../pages/SupportPage'));
const WorkspacesPage     = lazy(() => import('../pages/WorkspacesPage'));
const ClipUploadPage     = lazy(() => import('../pages/ClipUploadPage'));
const ClipsPage          = lazy(() => import('../pages/ClipsPage'));
const LabsPage           = lazy(() => import('../pages/LabsPage'));
const ArcsLandingPage    = lazy(() => import('../pages/ArcsLandingPage'));
const ExplorePage        = lazy(() => import('../pages/ExplorePage'));
const AdminPage          = lazy(() => import('../pages/AdminPage'));
const HomePage           = lazy(() => import('../pages/HomePage'));
const MyProfilePage      = lazy(() => import('../pages/MyProfilePage'));
const EpisodeEditorPage  = lazy(() => import('../pages/EpisodeEditorPage'));
const SearchPage         = lazy(() => import('../pages/SearchPage'));
const InvitePage         = lazy(() => import('../pages/InvitePage'));
const AccountPage        = lazy(() => import('../pages/AccountPage'));
const EarnCreditsPage    = lazy(() => import('../pages/EarnCreditsPage'));
const CreatorProfilePage = lazy(() => import('../pages/CreatorProfilePage'));
const ShowsManagerPage   = lazy(() => import('../pages/ShowsManagerPage'));
const ShowEditorPage     = lazy(() => import('../pages/ShowEditorPage'));
const LeaderboardPage    = lazy(() => import('../pages/LeaderboardPage'));
const InboxPage          = lazy(() => import('../pages/InboxPage'));
const CollabsPage        = lazy(() => import('../pages/CollabsPage'));
const MatchesPage        = lazy(() => import('../pages/MatchesPage'));

const THINKING_STEPS = [
  'Searching what\'s driving views in your niche right now',
  'Finding what gets clicks AND sponsors in this space',
  'Optimizing this for retention and revenue',
  'Building hooks designed to stop the scroll',
  'Structuring this for audience growth',
  'This one could actually make you money…',
  'Finalizing your production-ready episode package',
];

const INITIAL_BRIEF = {
  topic: '', mode: 'build', contentType: 'solo', goal: 'views',
  tone: 'calm', duration: '60', guest: '', audience: '', context: '', listenerFeeling: '',
};

// Mobile breakpoint mirrors AppShell's threshold for the V4 mobile layout.
function isMobileViewport() {
  return typeof window !== 'undefined'
    && window.matchMedia
    && window.matchMedia('(max-width: 768px)').matches;
}

// Hardcoded admin gate. Admins (Drew + Akaash) skip onboarding and go
// straight to the app; everyone else runs the onboarding flow first.
function isAppAdmin(user) {
  return isHardcodedAdminEmail(user?.email);
}

// TEMPORARY: onboarding is disabled while we redesign that flow. When
// this flips back to true the OnboardingFlow takeover returns. We
// deliberately don't mark onboarding_complete in the bypass path, so
// users who sign up during the disabled window still get the takeover
// on re-enable.
const ONBOARDING_ENABLED = false;

// Views that require an account. A logged-out visitor who lands on any
// of these (typed URL, stale bookmark, legacy hash) is bounced back to
// the public landing page — the app is gated behind sign-up.
const APP_ONLY_VIEWS = new Set([
  'shows', 'builder', 'brief', 'editor', 'dashboard', 'episodes',
  'analytics', 'marketplace', 'settings', 'billing', 'help', 'admin',
  'profile', 'explore', 'labs', 'search', 'invite', 'credits',
  'account', 'inbox', 'matches', 'shows-manager', 'show-editor',
  'clips', 'clip-upload', 'workspaces', 'pricing',
  'subscription', 'support', 'partners', 'guest-profile',
  'guest-profile-edit',
]);

// Default view when no path/hash override is present.
//   • Logged out → public landing page
//   • Signed in on mobile → Clips feed (short-form-first entry)
//   • Signed in on desktop → Home (the producer-mode dashboard)
function initialView(user) {
  // Escape hatch: `?view=landing` always shows the landing page, even
  // for signed-in users who'd normally cold-start on /shows or /clips.
  if (typeof window !== 'undefined') {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('view') === 'landing') return 'landing';
    } catch {}
  }
  if (!user) return 'landing';
  return isMobileViewport() ? 'clips' : 'shows';
}

// On mobile we don't show a home page — / and any 'shows' resolution
// becomes 'clips' before React ever mounts HomePage. Applied at both
// path-resolution sites so cold loads, deep links, and back-button
// navigation all skip the HomePage render entirely.
function applyMobilePreference(view) {
  if (view === 'shows' && isMobileViewport()) return 'clips';
  return view;
}

export default function EpisodeBuilderApp() {
  const { user, profile, loading, refreshProfile, isAdmin } = useAuth();
  const toast = useToast();
  const player = usePlayer();
  // Episode whose narration audio is currently being generated (drives the
  // spinner on the workshop thumb).
  const [audioLoadingId, setAudioLoadingId] = useState(null);

  const [authMode, setAuthMode]           = useState('signup');
  const [showAuth, setShowAuth]           = useState(false);
  const [showCommandPalette, setShowCommandPalette] = useState(false);
  // OnboardingFlow gate. showOnboarding flips true when an authenticated
  // user lands without onboarding_complete=true on their profile row;
  // onboardingEntry is 'chat' if there's a pending brief queued from
  // the landing chat, 'fresh' otherwise. Setting showOnboarding=false
  // fires the queued brief (if any) via handleOnboardingComplete.
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingEntry, setOnboardingEntry] = useState('fresh');
  const [view, setView]                   = useState(() => initialView(user));
  const [activeShowId, setActiveShowId]   = useState(null);
  const [activeEpisode, setActiveEpisode] = useState(null);
  // Landing-chat episode preview. When an anonymous visitor finishes the
  // hero chat, the generated (locked) document is stashed here and the
  // app renders the standalone EpisodePreviewPage with a sign-up gate.
  const [previewDoc, setPreviewDoc]       = useState(null);
  // Suno-style "just built" highlight. After a successful build, the
  // user stays on the Create surface and the new episode appears at
  // the top of the workspace right panel — we stash its id here so
  // RecentEpisodesFeed can flag + scroll to it.
  const [lastBuiltEpisodeId, setLastBuiltEpisodeId] = useState(null);
  const [brief, setBrief]                 = useState(INITIAL_BRIEF);
  // One-shot seed handed from the Home composer to the /create chat.
  // { topic, depth } when arriving from Home; cleared once EpisodeChat
  // consumes it so re-entering /create doesn't re-seed a stale topic.
  const [chatSeed, setChatSeed]           = useState(null);
  // Navigation history for the internal-page back button. navigate()
  // pushes the view it's leaving; handlePageBack() pops it.
  const navHistoryRef                     = useRef([]);
  // Mini-spec #67: track the in-progress draft id so subsequent
  // auto-saves UPDATE the same row instead of creating duplicates.
  const [activeDraftId, setActiveDraftId] = useState(null);

  // When ClipsPage fires "Remix this", it drops the inspiration prompt into
  // sessionStorage and navigates to 'brief'. Pick that up here so the
  // composer opens with the prompt already filled in.
  useEffect(() => {
    if (view !== 'brief') return;
    try {
      const seed = sessionStorage.getItem('episode_builder_seed');
      if (seed && seed.trim()) {
        setBrief(prev => ({ ...prev, topic: seed.trim(), mode: 'build' }));
        sessionStorage.removeItem('episode_builder_seed');
        sessionStorage.removeItem('episode_builder_seed_from_clip');
      }
    } catch { /* sessionStorage unavailable — no-op */ }
  }, [view]);

  // (The previous post-mount mobile redirect is gone — applyMobilePreference
  // is applied at the path-resolution sites instead, so /shows becomes
  // 'clips' BEFORE the view state is ever set. No HomePage render, no
  // flash, no extra round-trip.)
  const [thinkingIndex, setThinkingIndex] = useState(0);
  const [isBuilding, setIsBuilding]       = useState(false);
  const [error, setError]                 = useState('');
  const [episodes, setEpisodes]           = useState([]);
  const [workspaces, setWorkspaces]       = useState([]);
  // Separate list for tool='short_form' rows. Lives alongside `episodes`
  // so the Workspace right-panel can render two folders (Episodes /
  // Short-form) without further filtering.
  const [shortFormScripts, setShortFormScripts] = useState([]);
  const [shows, setShows]                 = useState([]);
  const [showsLoaded, setShowsLoaded]     = useState(false);
  const [openCreateShowFlag, setOpenCreateShowFlag] = useState(false);
  // Mini-spec #4: which show is being edited inside ShowEditorPage.
  const [editingShowId, setEditingShowId] = useState(null);
  // Episode id parsed from /editor/<id> so a refresh or shared link can
  // re-render the editor for the right episode. Set by the routing
  // effects; consumed by a useEffect that hydrates activeEpisode from
  // the loaded episodes list once both are available.
  const [editingEpisodeId, setEditingEpisodeId] = useState(null);
  const [notifPanelOpen, setNotifPanelOpen]         = useState(false);
  const [notifCount, setNotifCount]                 = useState(0);
  const [inboxCount, setInboxCount]                 = useState(0);
  const [remixBusyId, setRemixBusyId]               = useState(null);

  // ── Public creator deep-link (/@username) ────────────────────────────────
  // `publicCreator` holds a creator-shaped object fetched from
  // podcaster_profiles when the page loads at /@<username>. While non-null,
  // it renders a full-screen CreatorProfilePage in place of the normal app
  // shell — no auth required, so link recipients see the profile cleanly.
  const [publicCreator, setPublicCreator]             = useState(null);
  const [publicCreatorLoading, setPublicCreatorLoading] = useState(false);
  const [publicCreatorMissing, setPublicCreatorMissing] = useState(null); // handle string if not found

  const timerRef        = useRef(null);
  const pendingBriefRef = useRef(null);
  const abortRef        = useRef(false);
  // Guards the onboarding gate against re-firing on every Supabase
  // token refresh — the auth listener emits a new user object on each
  // refresh and tab focus, which would otherwise re-run the DB check.
  const onboardingCheckedForRef = useRef(null);

  // ── Cmd+K command palette ──────────────────────────────────────────────────
  useEffect(() => {
    function handleKey(e) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setShowCommandPalette(v => !v);
      }
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);

  // ── 'onboarding:signin' from OnboardingFlow's "Sign in" link ─────────────
  // Drops the onboarding overlay and opens the auth modal in login mode
  // so a returning user who arrived at /onboarding can switch to sign-in
  // without backtracking through the chat or landing.
  useEffect(() => {
    function handleOnboardingSignin() {
      setShowOnboarding(false);
      setAuthMode('login');
      setShowAuth(true);
    }
    window.addEventListener('onboarding:signin', handleOnboardingSignin);
    return () => window.removeEventListener('onboarding:signin', handleOnboardingSignin);
  }, []);

  // ── /@username → public creator lookup ───────────────────────────────────
  // Parses the path once on mount. If it matches `/@<handle>`, we fetch the
  // matching row from podcaster_profiles and render CreatorProfilePage as a
  // standalone view (auth-optional).
  useEffect(() => {
    const path = (typeof window !== 'undefined' && window.location.pathname) || '';
    const match = path.match(/^\/@([a-zA-Z0-9_-]{1,30})\/?$/);
    if (!match) return;
    const handle = match[1].toLowerCase();
    if (!supabase) {
      setPublicCreatorMissing(handle);
      return;
    }
    setPublicCreatorLoading(true);
    (async () => {
      try {
        // Mini-spec #62: case-insensitive handle lookup. The URL is
        // lowercased on the way in, but profiles in the DB may have
        // mixed-case usernames (legacy data, or signup flow that
        // didn't normalize). ilike with no wildcards is an exact
        // case-insensitive match.
        const { data } = await supabase
          .from('podcaster_profiles')
          .select('user_id, full_name, show_name, username, niche, avatar_url, avg_listeners, episodes_created')
          .ilike('username', handle)
          .maybeSingle();
        if (data) {
          const show = (data.show_name || '').trim() || (data.full_name || '').trim() || `@${handle}`;
          const host = (data.full_name || '').trim() || `@${handle}`;
          setPublicCreator({
            id: `real-${data.user_id}`,
            userId: data.user_id,
            show,
            host,
            niche: data.niche || '',
            audience: data.avg_listeners > 100000 ? '100K+'
              : data.avg_listeners > 10000 ? '10K\u201350K'
              : 'Growing',
            hookScore: Math.min(99, 75 + (data.episodes_created || 0)),
            c1: '#008ae2',
            c2: '#53167A',
            avatar_url: data.avatar_url || null,
            username: handle,
            isReal: true,
          });
        } else {
          setPublicCreatorMissing(handle);
        }
      } catch (err) {
        console.error('Creator lookup failed:', err);
        setPublicCreatorMissing(handle);
      } finally {
        setPublicCreatorLoading(false);
      }
    })();
  }, []);

  // ── Auth-driven view routing ───────────────────────────────────────────────
  // Path-based routing (mini-spec #10). On the landing→signed-in
  // transition we read the URL path for direct deep-links and normalize
  // any legacy hash. A visitor who signs up from the landing chat or the
  // episode-preview page is routed into the app. Logged-out visitors who
  // drift onto an app-only view are bounced back to the public landing
  // page — the app is gated behind sign-up.
  useEffect(() => {
    setView(current => {
      if (user && (current === 'landing' || current === 'episode-preview')) {
        const fromHash = normalizeLegacyHash();
        const fromPath = viewFromPath(window.location);
        if (fromHash?.view) return applyMobilePreference(fromHash.view);
        if (fromPath?.view) {
          if (fromPath.showId)    setEditingShowId(fromPath.showId);
          if (fromPath.episodeId) setEditingEpisodeId(fromPath.episodeId);
          return applyMobilePreference(fromPath.view);
        }
        // No URL override — pick the per-viewport default.
        return initialView(user);
      }
      if (!user && APP_ONLY_VIEWS.has(current) && !activeEpisode)
        return 'landing';
      return current;
    });
  }, [user, activeEpisode]);

  // Mini-spec #67: auto-save the brief to episode_drafts whenever it
  // meaningfully changes while the user is in the brief / builder
  // flow. Debounced 1500ms via lib/drafts.js so we don't write on
  // every keystroke. Skipped while view is 'thinking' or 'editor'
  // since the brief is locked once generation starts.
  useEffect(() => {
    if (!user?.id) return;
    if (!brief?.topic || !brief.topic.trim()) return;
    if (['thinking', 'editor', 'results'].includes(view)) return;
    autoSaveDraft(user.id, {
      id: activeDraftId || undefined,
      topic: brief.topic,
      brief,
      show_id: activeShowId || null,
    }).then(row => {
      if (row?.id && row.id !== activeDraftId) setActiveDraftId(row.id);
    });
  }, [user?.id, brief, activeShowId, view, activeDraftId]);

  useEffect(() => {
    if (!user?.id) { onboardingCheckedForRef.current = null; return; }
    setShowAuth(false);

    // Run at most once per user.id — Supabase fires onAuthStateChange on
    // every token refresh + tab focus with a fresh user object reference,
    // and we don't want to re-query the DB (or worse, re-show the
    // takeover during the round-trip) on every fire.
    if (onboardingCheckedForRef.current === user.id) return;
    onboardingCheckedForRef.current = user.id;

    // Admins skip onboarding entirely — straight into the app.
    if (isAppAdmin(user)) return;

    // Temporarily disabled — run the post-auth handoff directly so the
    // landing-chat pending generation still gets claimed and any
    // queued brief still fires. Skip when ONBOARDING_ENABLED flips
    // back to true.
    if (!ONBOARDING_ENABLED) {
      runPostAuthHandoff();
      return;
    }

    // Onboarding gate. New users (no onboarding_complete) see the
    // OnboardingFlow takeover first; returning users skip straight to
    // the brief handoff. The check is async (profile may not be loaded
    // yet) and falls back to the legacy brief-handoff on RPC error so a
    // hiccup never strands the user in an empty state.
    const checkOnboarding = async () => {
      try {
        const { data } = await supabase
          .from('podcaster_profiles')
          .select('onboarding_complete')
          .eq('user_id', user.id)
          .maybeSingle();

        const needsOnboarding = !data?.onboarding_complete;

        if (needsOnboarding) {
          const hasPendingChat = (() => {
            try { return !!sessionStorage.getItem('pending_brief_v1'); }
            catch { return false; }
          })();
          setOnboardingEntry(hasPendingChat ? 'chat' : 'fresh');
          setShowOnboarding(true);
          // Do NOT call handleBuildEpisode yet — handleOnboardingComplete
          // will fire it once the user finishes the flow.
        } else {
          // Returning user — same brief-handoff flow as before. Mini-spec
          // #26: also check sessionStorage in case the user signed up
          // via Google OAuth (the OAuth round-trip remounts the React
          // app and wipes pendingBriefRef).
          let queued = pendingBriefRef.current;
          if (!queued) {
            try {
              const raw = sessionStorage.getItem('pending_brief_v1');
              if (raw) queued = JSON.parse(raw);
            } catch {}
          }
          if (queued) {
            pendingBriefRef.current = null;
            try { sessionStorage.removeItem('pending_brief_v1'); } catch {}
            handleBuildEpisode(queued);
          }
        }
      } catch (e) {
        console.error('Onboarding check failed:', e);
        // On error, fall through to the legacy flow so the user still
        // gets their queued brief built. They'll see the onboarding
        // next time their profile loads cleanly.
        let queued = pendingBriefRef.current;
        if (queued) {
          pendingBriefRef.current = null;
          handleBuildEpisode(queued);
        }
      }
    };

    checkOnboarding();
  }, [user?.id]); // eslint-disable-line

  // ── URL path → view sync (mini-spec #10) ──────────────────────────────────
  // Path-based routing replaced the legacy hash sync. Listens for popstate
  // (browser back/forward) AND a custom 'app:navigate' event some
  // components dispatch when they push state imperatively.
  useEffect(() => {
    function applyPath() {
      if (!user) return;
      // Legacy-hash recovery — handles edge cases where a user navigates
      // to an old bookmarked URL mid-session.
      const fromHash = normalizeLegacyHash();
      if (fromHash?.view) {
        const next = applyMobilePreference(fromHash.view);
        setView(current => (current === next ? current : next));
        return;
      }
      const resolved = viewFromPath(window.location);
      if (!resolved) return;          // /@<handle> and unknown paths
      // Mini-spec #12: legacy URL canonicalization (e.g. /inbox → /messages).
      if (resolved.redirectTo && window.location.pathname !== resolved.redirectTo) {
        try { window.history.replaceState(null, '', resolved.redirectTo); } catch {}
      }
      if (resolved.showId)    setEditingShowId(resolved.showId);
      if (resolved.episodeId) setEditingEpisodeId(resolved.episodeId);
      const next = applyMobilePreference(resolved.view);
      setView(current => (current === next ? current : next));
    }
    // Custom event: descendant components (NotificationBell, etc.) ask
    // the host to navigate without threading an `onNavigate` prop down
    // the tree. detail = { view, opts }.
    function onAppNavigate(e) {
      const target = e.detail?.view;
      if (!target) return;
      navigate(target, e.detail?.opts || {});
    }
    applyPath();
    window.addEventListener('popstate', applyPath);
    window.addEventListener('app:navigate', onAppNavigate);
    return () => {
      window.removeEventListener('popstate', applyPath);
      window.removeEventListener('app:navigate', onAppNavigate);
    };
    // Key on user.id, NOT the user reference, so the path-sync doesn't
    // re-fire on every Supabase TOKEN_REFRESHED event (which emits a
    // fresh user object on the same id). Token refresh used to bounce
    // editor views back to /library because applyPath would re-read
    // the URL and snap view back to whatever the pathname said.
  }, [user?.id]); // eslint-disable-line

  // ── Episode + short-form loading ───────────────────────────────────────────
  const loadEpisodes = useCallback(async () => {
    if (!user) { setEpisodes([]); setShortFormScripts([]); setWorkspaces([]); return; }
    try {
      // Parallel fetch — episodes + short-form (same table, filtered by
      // `tool`) plus the user's workspace folders so the Workshop panel can
      // group items by folder, not just by type.
      const [eps, shorts, ws] = await Promise.all([
        getEpisodes(user.id),
        getShortFormScripts(user.id),
        getWorkspaces(user.id),
      ]);
      setEpisodes(eps || []);
      setShortFormScripts(shorts || []);
      setWorkspaces(ws || []);
    } catch { /* non-fatal */ }
  }, [user]);

  useEffect(() => { loadEpisodes(); }, [loadEpisodes]);

  // Warm the Home/dashboard chunk during idle time so navigating Home from
  // elsewhere in the app doesn't wait on a chunk download. Prefetch only —
  // no render, no fetch; it just primes the same lazy() module cache.
  useEffect(() => {
    const ric = window.requestIdleCallback || ((cb) => setTimeout(cb, 1200));
    const cancel = window.cancelIdleCallback || clearTimeout;
    const id = ric(() => { importDashboard().catch(() => {}); });
    return () => cancel(id);
  }, []);

  // Hydrate activeEpisode from the URL on refresh / direct link. When
  // the user lands on /editor/<id> with no in-memory activeEpisode
  // (page reload, tab restore, shared URL), find the matching row in
  // the loaded episodes list and set it as active. Without this the
  // editor view renders nothing and the user falls back to a blank
  // screen that looks like the library.
  useEffect(() => {
    if (view !== 'editor') return;
    if (!editingEpisodeId) return;
    if (activeEpisode?.id === editingEpisodeId) return;
    if (!Array.isArray(episodes) || episodes.length === 0) return;
    const found = episodes.find(ep => ep && ep.id === editingEpisodeId);
    if (!found) return;
    setActiveEpisode({
      ...found,
      brief: { topic: found.topic, contentType: found.format, ...INITIAL_BRIEF },
    });
    if (found.show_id) setActiveShowId(prev => prev || found.show_id);
  }, [view, editingEpisodeId, episodes, activeEpisode?.id]);

  // ── Shows loading (for sidebar nav + auto-skip + builder selector) ────────
  const loadShows = useCallback(async () => {
    if (!user) { setShows([]); setShowsLoaded(true); return; }
    try {
      await ensureDefaultShow(user.id, profile);
      const list = await getShows(user.id);
      setShows(list || []);
    } catch (e) {
      console.error('loadShows error:', e);
    } finally {
      setShowsLoaded(true);
    }
  }, [user, profile]);

  useEffect(() => { loadShows(); }, [loadShows]);

  // Seed + load notification count whenever we have a user
  useEffect(() => {
    if (!user?.id) { setNotifCount(0); return; }
    seedIfEmpty(user.id, profile);
    setNotifCount(unreadCount(user.id));
  }, [user?.id, profile]);

  const refreshNotifCount = useCallback(() => {
    if (!user?.id) return;
    setNotifCount(unreadCount(user.id));
  }, [user?.id]);

  // Refresh the inbox badge — a pitch arrived since the last time the user
  // opened Inbox. We re-fetch when the user lands on non-inbox views so the
  // number stays live without polling.
  const refreshInboxCount = useCallback(async () => {
    if (!user?.id) { setInboxCount(0); return; }
    try {
      const rows = await getIncomingRequests(user.id);
      setInboxCount(countUnreadPitches(user.id, rows));
    } catch { /* non-fatal */ }
  }, [user?.id]);

  useEffect(() => {
    refreshInboxCount();
  }, [refreshInboxCount, view]);

  // On initial load, set the first show as active (used as the default
  // show context for new episodes). We no longer auto-redirect to the
  // builder — the Home view now *is* the composer surface.
  useEffect(() => {
    if (!user || !showsLoaded) return;
    if (!activeShowId && shows.length > 0) {
      setActiveShowId(shows[0].id);
    }
  }, [user, shows, showsLoaded, activeShowId]);

  // Renew credits if ≥30 days have elapsed since last cycle (lazy, no-op
  // if fresh). Fire-and-forget; profile refresh happens via AuthContext.
  useEffect(() => {
    if (!user?.id) return;
    renewCreditsIfDue(user.id).then(() => refreshProfile?.()).catch(() => {});
  }, [user?.id, refreshProfile]);

  // ── Usage counting ─────────────────────────────────────────────────────────
  const usesLeft = useMemo(() => {
    if (user) return Math.max(0, FREE_LIMIT - (profile?.episodes_created || 0));
    return Math.max(0, FREE_LIMIT - getGuestUses());
  }, [profile, user]);

  // ── Navigation (mini-spec #10: path-based) ─────────────────────────────
  // Writes the canonical URL via pushState so the back button works AND
  // the address bar always reflects the current view. /manage-shows/<id>
  // is special-cased: callers pass `opts.showId` when navigating into
  // the show editor; we both update editingShowId and put the id in the
  // URL atomically so React state and the address bar stay in sync.
  const navigate = (targetView, opts = {}) => {
    // Record where we're leaving so the internal-page back button can
    // return there. Skip no-op navigations + cap the stack.
    if (targetView !== view) {
      navHistoryRef.current.push(view);
      if (navHistoryRef.current.length > 50) navHistoryRef.current.shift();
    }
    if (targetView === 'brief') {
      setActiveEpisode(null);
      setBrief(INITIAL_BRIEF);
      setError('');
    }
    if (targetView === 'show-editor' && opts.showId) {
      setEditingShowId(opts.showId);
    }
    setView(targetView);
    try {
      const showId = (targetView === 'show-editor')
        ? (opts.showId || editingShowId)
        : null;
      const path = pathFor(targetView, showId ? { showId } : {});
      if (path && path !== window.location.pathname) {
        window.history.pushState(null, '', path);
      }
    } catch {}
  };

  const startNewEpisode = () => {
    setActiveEpisode(null);
    setBrief(INITIAL_BRIEF);
    setError('');
    setView(user ? 'brief' : 'landing');
  };

  const handleStartWithTopic = (topicText, mode = 'build') => {
    setBrief(prev => ({ ...prev, topic: topicText || '', mode }));
    setActiveEpisode(null);
    setError('');
    setView('builder');
  };

  // Shared hand-off into the Create chat with a prompt pre-filled — used by
  // the Home page's contextual cards and the global "Ask epio or search"
  // bar (Home Redesign). Seeds a one-shot Simple-mode chat turn so the
  // conversation continues naturally in /create instead of opening blank.
  const handleAskEpio = (topic, format) => {
    setActiveEpisode(null);
    const t = (topic || '').trim();
    setBrief(prev => ({ ...prev, topic: t, ...(format ? { format } : {}) }));
    setChatSeed(t ? { topic: t, depth: 'simple' } : null);
    setError('');
    setView('builder');
  };

  const handleBuildFromTrend = (trend) => {
    addXP('trend_used');
    setBrief(prev => ({
      ...prev,
      topic: trend.topic || '',
      mode: 'build',
      trendContext: trend.trendContext || '',
    }));
    setActiveEpisode(null);
    setError('');
    setView('builder');
  };

  // Remix an existing episode into a fresh spin-off topic, then drop the
  // user back into the Builder with the new topic pre-filled. No credits
  // consumed here — the idea itself is free; they only pay when they build.
  const handleRemixEpisode = async (ep) => {
    if (!ep || remixBusyId) return;
    setRemixBusyId(ep.id);
    try {
      const show = shows.find(s => s.id === ep.show_id) || shows[0];
      const { title, description, angle } = await fetchRemixIdea({
        sourceEpisode: ep,
        niche: show?.niche || profile?.niche || '',
        showName: show?.name || '',
        audience: show?.target_audience || '',
      });
      if (show?.id) setActiveShowId(show.id);
      setBrief({
        ...INITIAL_BRIEF,
        topic: title,
        mode: 'build',
        contentType: ep.format || 'solo',
        context: [description, angle, `(Inspired by: ${ep.topic || ep.result?.title || 'previous episode'})`]
          .filter(Boolean).join('\n\n'),
        // Saved back to the DB so the source episode's "Remixes" tab picks
        // this child up.
        remixed_from_id: ep.id,
      });
      setActiveEpisode(null);
      setError('');
      setView('builder');
      toast.success(`New spin ready: "${title}"`);
    } catch (err) {
      console.error('handleRemixEpisode failed:', err);
      toast.error(err?.message || "Couldn't build that spin. Try again.");
    } finally {
      setRemixBusyId(null);
    }
  };

  const handleSelectEpisode = (ep) => {
    if (!ep?.result) return;
    setActiveEpisode({ ...ep, brief: { topic: ep.topic, contentType: ep.format, ...INITIAL_BRIEF } });
    // Also sync the active show so the editor's show context is right
    if (ep.show_id) setActiveShowId(ep.show_id);
    setView('editor');
    // Push /editor/<id> so refresh, back/forward, and tab-return all
    // bring the user back to this specific episode instead of a blank
    // editor (which falls through to Library on mount when activeEpisode
    // is null).
    try {
      const target = ep?.id ? `/editor/${ep.id}` : '/editor';
      if (window.location.pathname !== target) {
        window.history.pushState(null, '', target);
      }
    } catch {}
  };

  // ── Episode narration playback ──────────────────────────────────────────────
  // Export the current episode's narration MP3. Costs 1 credit (listening is
  // free; only the download is metered). On insufficient credits we stop; on
  // any other hiccup we still let the download through.
  const handleExportAudio = useCallback(async (ep, url) => {
    if (!url) return;
    try {
      if (user?.id) {
        await deductForAction(user.id, 'audio_export', { episode_id: ep.id });
        refreshProfile?.();
      }
    } catch (e) {
      if (isInsufficientCreditsError(e)) {
        toast.error('Not enough credits to export. Top up to download the MP3.');
        return;
      }
      console.warn('audio_export deduct failed:', e?.message || e);
    }
    try {
      const a = document.createElement('a');
      a.href = url;
      const safe = (ep.topic || 'episode').replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '').slice(0, 60) || 'episode';
      a.download = `${safe}.mp3`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success('Exported MP3.');
    } catch {
      toast.error('Could not start the download.');
    }
  }, [user, toast, refreshProfile]);

  // Play an episode's narration in the bottom player. Generates the MP3 once
  // (cached in Storage thereafter), or toggles play/pause if it's already the
  // loaded track.
  // Build a player track for an episode. `preview` sets a ~60s preview window
  // (used by the collection playlist); `url` is left null + `resolve` lazy so
  // downstream queue tracks don't generate audio until the player lands on
  // them. Workshop episodes have no user_id (always the viewer's own);
  // collection episodes carry user_id, so we only cache-upload when owned.
  const makeAudioTrack = useCallback((ep, { preview = false, url = null } = {}) => {
    const show = shows.find(s => s.id === ep.show_id) || shows[0];
    const creator = ep.creator || null;
    const owned = ep.user_id ? ep.user_id === user?.id : true;
    const track = {
      url,
      episodeId: ep.id,
      title:     ep.title || ep.topic || 'Untitled episode',
      subtitle:  creator
        ? (creator.full_name || (creator.username ? `@${creator.username}` : 'Episode narration'))
        : 'Episode narration',
      coverUrl:  ep.result?.cover_url || show?.cover_art_url || null,
      previewSeconds: preview ? 60 : null,
      onCreatorClick: creator ? () => openCreatorByUsername(creator, () => navigate('explore')) : null,
      resolve:   () => getEpisodeAudioUrl(ep, { userId: owned ? user?.id : null }),
    };
    // Export uses whatever url is resolved at click time (mutated onto track).
    track.onExport = () => handleExportAudio(ep, track.url);
    return track;
  }, [shows, user, navigate, handleExportAudio]);

  // Play an episode. From the workshop it's a single track; from a collection
  // row (queueEpisodes) it's a 60s-preview playlist that auto-advances.
  const handlePlayEpisode = useCallback(async (ep, queueEpisodes) => {
    if (!ep?.id) return;
    if (player.track?.episodeId === ep.id) { player.toggle(); return; }
    if (audioLoadingId) return; // one generation at a time
    setAudioLoadingId(ep.id);
    try {
      const isCollection = Array.isArray(queueEpisodes) && queueEpisodes.length > 1;
      if (isCollection) {
        const startIndex = Math.max(0, queueEpisodes.findIndex(e => e.id === ep.id));
        const queue = queueEpisodes.map(e => makeAudioTrack(e, { preview: true }));
        // Resolve just the starting track so playback begins immediately; the
        // rest resolve lazily as the player advances.
        queue[startIndex].url = await queue[startIndex].resolve();
        player.playQueue(queue, startIndex);
      } else {
        const track = makeAudioTrack(ep, { preview: false });
        track.url = await track.resolve();
        player.playTrack(track);
      }
    } catch (e) {
      toast.error(e?.message || 'Could not play this episode.');
    } finally {
      setAudioLoadingId(null);
    }
  }, [player, audioLoadingId, makeAudioTrack, toast]);

  // ── Core generation ────────────────────────────────────────────────────────
  const handleBuildEpisode = async (nextBrief) => {
    setError('');

    if (!user && getGuestUses() >= FREE_LIMIT) {
      pendingBriefRef.current = nextBrief;
      setAuthMode('signup');
      setShowAuth(true);
      return;
    }

    // Authenticated: gate on monthly credits before doing anything expensive.
    if (user) {
      // Balances live on the user_credits row, NOT on podcaster_profiles.
      // Passing `profile` here made balance/topup_balance read as 0 for every
      // user, so canBuild was always false and NOBODY could generate — except
      // accounts with unlimited_credits (admins), which short-circuit below.
      // Merge the flag in so that bypass still works.
      const creditsRow = await getUserCredits(user.id).catch(() => null);
      const credits = summarizeCredits(profile?.plan, {
        ...(creditsRow || {}),
        unlimited_credits: profile?.unlimited_credits,
      });
      if (!credits.canBuild) {
        const msg = credits.nextTier
          ? `You're out of credits this cycle. Upgrade to ${credits.nextTier.label} for more.`
          : `You're out of credits this cycle.`;
        toast.error(msg);
        setError(msg);
        return;
      }
    }

    abortRef.current = false;
    setBrief(nextBrief);
    // Optimistic workspace tile — Suno does this. The user clicks Create
    // and a small square (with topic + "Building…" status) appears at
    // the top of the right-panel workspace immediately. The placeholder
    // gets swapped for the saved row when the API returns. Pending
    // episodes are flagged with `pending: true` so the feed can render
    // a shimmer + disable click. Only do this for signed-in users on
    // the builder view — guests still get the full-screen ThinkingView
    // because they don't have a workspace to land in.
    const stayOnBuilder = !!user && (view === 'builder' || view === 'brief');
    const pendingId = `pending-${Date.now()}`;
    if (stayOnBuilder) {
      setLastBuiltEpisodeId(pendingId);
      setEpisodes(prev => [{
        id:           pendingId,
        topic:        nextBrief.topic,
        format:       nextBrief.contentType,
        show_id:      nextBrief.show_id || activeShowId || null,
        created_at:   new Date().toISOString(),
        pending:      true,
        result:       null,
      }, ...prev]);
    } else {
      setView('thinking');
    }
    setIsBuilding(true);
    setThinkingIndex(0);

    let step = 0;
    // 2 s per step — lets users read each one before the final loading-bar step
    timerRef.current = setInterval(() => {
      step += 1;
      setThinkingIndex(c => Math.min(c + 1, THINKING_STEPS.length - 1));
      if (step >= THINKING_STEPS.length - 1) clearInterval(timerRef.current);
    }, 2000);

    try {
      // New v2 pipeline: a single unified "episode brief" document generated
      // in ONE API call, with the model routed by the user-selected quality
      // tier (draft/standard/premium → haiku/sonnet/opus). Legacy multi-tab
      // generateEpisode() is still exported for backwards compat but no
      // longer called here — it was a ~1200-token system prompt + 8000
      // max_tokens per build, which was the primary cost driver.
      const quality = normalizeQuality(nextBrief.quality || 'standard');
      const document = await generateEpisodeBrief(nextBrief, profile || {}, quality);

      const episodeRecord = {
        id: `local-${Date.now()}`,
        topic: nextBrief.topic,
        format: nextBrief.contentType,
        created_at: new Date().toISOString(),
        result: {
          document,          // the new unified doc — primary surface
          quality,
          schema_version: 2, // lets the editor branch cleanly on legacy vs v2
        },
      };

      if (user) {
        try {
          // If the user didn't pick a workspace, default to their
          // "Episodes" workspace (lazily created on first save).
          let workspaceId = nextBrief.workspace_id || null;
          if (!workspaceId) {
            workspaceId = await ensureEpisodesWorkspace(user.id);
          }
          const saved = await saveEpisode(user.id, {
            topic: nextBrief.topic,
            format: nextBrief.contentType,
            niche: profile?.niche || '',
            result: episodeRecord.result,
            show_id: nextBrief.show_id || activeShowId || null,
            remixed_from_id: nextBrief.remixed_from_id || null,
            workspace_id: workspaceId,
          });
          episodeRecord.id = saved?.id || episodeRecord.id;
          // Deduct the cost for an episode build at the user's selected
          // generation tier (v4.5 / v4.6 / v4.8 selector in the chat). The
          // server RPC handles balance drain (monthly → top-up) + ledger.
          try {
            await deductForAction(user.id, episodeBuildCreditAction(), {
              episode_id: saved?.id,
              topic:      (saved?.topic || '').slice(0, 80),
            });
            window.dispatchEvent(new Event('credits:changed'));
            refreshProfile?.();
          } catch (creditErr) {
            // We gated on credits upfront, so this should be rare. Log and move on.
            console.error('deductForAction post-save error:', creditErr);
          }
          // Gamification: award XP and update streak (legacy localStorage-
          // based system) + the new mini-spec #5 server-side streak +
          // time-saved tally.
          updateStreak();
          addXP('episode_built');
          recordSuccessfulBuild(user.id, episodeBuildCreditAction()).catch(() => {});
          // Pattern log: remember what this creator makes episodes about, so
          // the producer + generation personalize over time (read back via
          // voiceProfileEpisodeBlock in episodeBrief). Deduped server-side;
          // fire-and-forget so it never blocks or breaks the build.
          recordTopic(user.id, nextBrief.topic).catch(() => {});
          const epCount = (profile?.episodes_created || 0) + 1;
          checkMilestones(epCount);
          loadEpisodes();
          // Mini-spec #67: this brief turned into a real episode —
          // mark the draft consumed so it drops off the Drafts list
          // (still preserved server-side for audit), and clear the
          // active id so the next build starts a fresh draft.
          if (activeDraftId) {
            try { await markConsumed(user.id, activeDraftId); } catch {}
            setActiveDraftId(null);
          }
        } catch (saveErr) {
          console.error('Save error:', saveErr);
          // Surface the real cause so the user can act on it. Most
          // common: missing migration column, RLS denial, or expired
          // session — all of which need real action, not "check your
          // connection".
          const reason = saveErr?.message || 'Unknown error';
          toast.warn(`Episode built but not saved: ${reason}`);
        }
      } else {
        incGuestUses();
      }

      if (!abortRef.current) {
        // Suno-style: stay on Create. The new episode appears at the top
        // of the workspace right panel; the user can keep generating or
        // click into it from the panel. (Previously we pushed straight
        // to the editor, which meant they couldn't see what they just
        // made in context with everything else.)
        setActiveEpisode({ ...episodeRecord, brief: nextBrief });
        setLastBuiltEpisodeId(episodeRecord.id);
        setView('builder');
        toast.success('Episode added to your workspace.');
      }
    } catch (err) {
      console.error('Build error:', err);
      const msg = err?.message || 'Episode build failed. Try again.';
      setError(msg);
      toast.error(msg);
      setView('builder');
    } finally {
      clearInterval(timerRef.current);
      setThinkingIndex(THINKING_STEPS.length - 1);
      setIsBuilding(false);
      refreshProfile?.();
    }
  };

  const handlePodcastAction = async (action) => {
    if (!activeEpisode) return;
    const baseContext = activeEpisode.brief?.context ? `${activeEpisode.brief.context}\n\n` : '';
    await handleBuildEpisode({
      ...activeEpisode.brief,
      context: `${baseContext}Refinement request: ${action}`,
    });
  };

  const handleImproveEpisode = (ep) => {
    if (!ep) return;
    setBrief({
      ...INITIAL_BRIEF,
      topic: ep.topic || '',
      contentType: ep.format || 'solo',
      mode: 'build',
      context: `Rebuild and improve this episode with stronger hooks, higher viral potential, and more emotionally specific talking points. Original topic: ${ep.topic}`,
    });
    setActiveEpisode(null);
    setError('');
    setView('builder');
  };

  const handlePackageEpisode = (ep) => {
    if (!ep?.result) return;
    setActiveEpisode({
      ...ep,
      brief: { topic: ep.topic, contentType: ep.format, ...INITIAL_BRIEF },
      initialTab: 'packaging',
    });
    if (ep.show_id) setActiveShowId(ep.show_id);
    setView('editor');
  };

  // ── Mobile deep-view navigation ───────────────────────────────────────────
  const isDeepView = ['brief', 'thinking', 'results'].includes(view);
  const canGoBack  = view === 'brief';
  const showExit   = view === 'brief' || view === 'results';

  // Internal "drill-in" pages get a back button. Explicit allowlist so it
  // never shows on a primary nav destination (home, explore, create, etc.).
  const INTERNAL_PAGES = [
    'profile', 'account', 'settings', 'help', 'support', 'pricing',
    'subscription', 'billing', 'credits', 'invite', 'shows-manager',
    'show-editor', 'leaderboard', 'guest-profile', 'guest-profile-edit',
    'analytics', 'matches', 'marketplace', 'partners', 'inbox',
  ];
  const isInternalPage = !!user && INTERNAL_PAGES.includes(view);

  const goHome = () => (user ? 'shows' : 'landing');

  const handleBack = () => {
    setError('');
    setView(goHome());
  };

  // Back button for internal pages — pops the nav history, falling back
  // to home if there's nothing to return to.
  const handlePageBack = () => {
    setError('');
    const prev = navHistoryRef.current.pop();
    setView(prev && prev !== view ? prev : goHome());
  };

  // Open another creator's profile in-app. Used as the fallback for tiles
  // whose creator has no username (so they can't deep-link to /@handle),
  // and directly from Search. Tolerant of both the Explore creator shape
  // (host_full_name / host_avatar_url) and the Search shape (full_name /
  // avatar_url).
  const openCreatorInApp = (c) => {
    if (!c?.user_id) return;
    if (c.user_id === user?.id) { navigate('profile'); return; }
    const name = (c.full_name || c.host_full_name || c.host_name || '').trim();
    const show = (c.show_name || '').trim() || name || `@${c.username || 'creator'}`;
    const host = name || `@${c.username || 'creator'}`;
    setPublicCreator({
      id: `real-${c.user_id}`,
      userId: c.user_id,
      show,
      host,
      niche: c.niche || '',
      audience: 'Growing',
      hookScore: 75,
      c1: '#008ae2',
      c2: '#53167A',
      avatar_url: c.avatar_url || c.host_avatar_url || null,
      username: c.username || '',
      isReal: true,
    });
    try { window.history.replaceState(null, '', `/@${c.username || c.user_id}`); } catch {}
  };

  const handleExitToHome = () => {
    abortRef.current = true;
    clearInterval(timerRef.current);
    setIsBuilding(false);
    setError('');
    setView(goHome());
  };

  const openAuth = (mode = 'signup') => { setAuthMode(mode); setShowAuth(true); };
  const handleSignOut = async () => { await signOut(); };

  // The landing hero chat finished a build for an anonymous visitor.
  // Stash the (locked) document, persist it so it survives the sign-up
  // round-trip, and route to the standalone EpisodePreviewPage. Works
  // for both full episodes and short-form soundbites — the kind comes
  // off the doc itself (doc.kind === 'soundbites').
  const handleEpisodePreview = (doc, generationId) => {
    if (!doc) return;
    setPreviewDoc(doc);
    const isSoundbites = doc?.kind === 'soundbites';
    const topic = isSoundbites
      ? (doc?.inputs?.topic || 'Your script')
      : (doc?.title || doc?.topic || 'Your episode');
    setPendingEpisode({
      doc,
      topic,
      kind: isSoundbites ? 'soundbites' : 'episode',
      generationId: generationId || null,
      savedAt: Date.now(),
    });
    setView('episode-preview');
  };

  // Auto-claim handoff from the landing chat. Called at the end of
  // OnboardingFlow when a pending generation is in localStorage:
  // persists the doc under the new user's account (full episode via
  // saveEpisode, short-form via saveShortFormScript) and lands them on
  // /builder with the workspace open — Suno-style. The new row shows
  // up in the right-panel Recents (highlighted for episodes), and the
  // chat composer sits right there for the next build.
  const claimPendingFromLanding = async (pending) => {
    if (!pending?.doc || !user) return;
    const isSoundbites = pending.kind === 'soundbites' || pending.doc?.kind === 'soundbites';
    try {
      if (isSoundbites) {
        const meta = pending.doc?.inputs || {};
        await saveShortFormScript(user.id, {
          topic:          pending.topic || meta.topic || 'Short-form script',
          tone:           meta.tone || null,
          hook_style:     meta.hook_style || null,
          target_seconds: meta.duration_sec || null,
          result:         pending.doc,
        });
      } else {
        const result = { document: pending.doc, quality: 'standard', schema_version: 2 };
        const saved = await saveEpisode(user.id, {
          topic:   pending.topic || 'Your episode',
          format:  pending.doc?.format || 'solo',
          niche:   profile?.niche || '',
          result,
          show_id: activeShowId || null,
          // Auto-file into the default "Episodes" folder so nothing lands unsorted.
          workspace_id: await ensureEpisodesWorkspace(user.id),
        });
        if (saved?.id) setLastBuiltEpisodeId(saved.id);
        loadEpisodes();
      }
    } catch (e) {
      console.error('Claim from landing failed:', e);
      toast.warn("Saved your sign-up — couldn't write the result to your library, but you can rebuild it from the chat.");
    }
    setView('builder');
  };

  // CreateView recovery banner — surfaced when the chat's localStorage
  // holds a built doc that never made it into the user's library
  // (auto-claim failed, sign-up round-trip raced, etc.). Saves the
  // doc same as the landing claim, opens episodes in the editor
  // immediately so the creator can read / export, and toasts for
  // short-form (no editor surface for those).
  const handleSaveUnsavedFromChat = async ({ mode, doc, generationId, openAfter = true }) => {
    if (!doc || !user) return;
    const isSoundbites = mode === 'soundbites' || doc?.kind === 'soundbites';

    // Dedupe — every generated doc carries a unique generatedAt
    // timestamp. If we've already saved this exact doc (auto-save
    // fired, recovery banner re-clicked, etc.), skip the insert and
    // just open the existing row in the editor instead of creating
    // another duplicate library entry.
    const docFingerprint = doc?.generatedAt || null;
    if (!isSoundbites && docFingerprint) {
      const existing = episodes.find(
        ep => ep?.result?.document?.generatedAt === docFingerprint,
      );
      if (existing) {
        if (openAfter) handleSelectEpisode(existing);
        return;
      }
    }
    try {
      if (isSoundbites) {
        const meta = doc?.inputs || {};
        await saveShortFormScript(user.id, {
          topic:          meta.topic || 'Short-form script',
          tone:           meta.tone || null,
          hook_style:     meta.hook_style || null,
          target_seconds: meta.duration_sec || null,
          result:         doc,
        });
        if (openAfter) toast.success('Saved to your library.');
      } else {
        const result = { document: doc, quality: 'standard', schema_version: 2 };
        const saved = await saveEpisode(user.id, {
          topic:        doc?.title || 'Your episode',
          format:       doc?.format || 'solo',
          niche:        profile?.niche || '',
          result,
          show_id:      activeShowId || null,
          // Explicit "Save to… Workshop" pick wins; otherwise auto-file into
          // the default "Episodes" folder so nothing lands unsorted.
          workspace_id: doc?.workspaceId || await ensureEpisodesWorkspace(user.id),
        });
        if (saved?.id) setLastBuiltEpisodeId(saved.id);
        loadEpisodes();
        // Recovery-banner path opens the new episode in the editor so
        // the creator can download / export immediately. The
        // in-session auto-save (openAfter:false) keeps them on /builder
        // — the new row surfaces via the right-panel Recents feed and
        // they can keep building without a context switch.
        if (openAfter) {
          handleSelectEpisode({
            id:      saved?.id || `local-${Date.now()}`,
            topic:   doc?.title || 'Your episode',
            format:  doc?.format || 'solo',
            show_id: activeShowId || null,
            result,
          });
        }
      }
    } catch (e) {
      console.error('handleSaveUnsavedFromChat:', e);
      // Surface the real cause so the user can act on it instead of
      // silently retrying. saveEpisode / saveShortFormScript already
      // wrap their errors with actionable detail (RLS, missing column,
      // expired session, etc.); pass that through to the toast.
      const reason = e?.message || 'Unknown error';
      if (openAfter) {
        toast.error(`Save failed: ${reason}`);
      } else {
        // Auto-save runs silently per the build-and-keep-going UX —
        // a soft warning is enough; the recovery banner picks it up
        // on the next /builder mount.
        toast.warn(`Couldn't auto-save: ${reason}`);
      }
      throw e; // let the caller decide whether to retry
    }
  };

  // Called by OnboardingFlow.onComplete when the user reaches screen 4.
  // Persists the answers via updateProfile (which uses the 3-tier upsert
  // fallback in lib/supabase.js — never raw .upsert()), refreshes the
  // AuthContext profile so the rest of the app sees the new values, then
  // unmounts the flow and fires the queued brief if one exists. If
  // there's no queued brief the user lands on the default view.
  const handleOnboardingComplete = async (data) => {
    try {
      await updateProfile(user.id, {
        show_status:         data.show_status || null,
        reactivation_user:   data.reactivation_user || false,
        show_name:           data.show_name || null,
        niche:               data.niches?.[0] || null,
        onboarding_complete: true,
      });
      refreshProfile?.();
    } catch (e) {
      console.error('Onboarding save failed:', e);
      // Non-fatal — proceed so the user isn't stranded mid-flow.
    }

    setShowOnboarding(false);
    await runPostAuthHandoff();
  };

  // Shared by handleOnboardingComplete and the onboarding-disabled
  // bypass. Pending generation from the landing chat? Auto-claim and
  // land on /builder. Otherwise fire any queued brief and fall through
  // to the default view.
  const runPostAuthHandoff = async () => {
    const pending = getPendingEpisode();
    if (pending) {
      clearPendingEpisode();
      await claimPendingFromLanding(pending);
      return;
    }
    let queued = pendingBriefRef.current;
    if (!queued) {
      try {
        const raw = sessionStorage.getItem('pending_brief_v1');
        if (raw) queued = JSON.parse(raw);
      } catch {}
    }
    if (queued) {
      pendingBriefRef.current = null;
      try { sessionStorage.removeItem('pending_brief_v1'); } catch {}
      handleBuildEpisode(queued);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  // If the URL matched `/@<username>`, render the creator profile full-screen
  // (auth-optional). A simple back arrow clears the deep-link and drops the
  // visitor back onto landing/home.
  if (publicCreator) {
    return (
      <>
        <SessionGuard />
        <Suspense fallback={<Spinner center />}>
          <CreatorProfilePage
            creator={publicCreator}
            onBack={() => {
              setPublicCreator(null);
              try { window.history.replaceState(null, '', '/'); } catch {}
            }}
            onOpenEpisode={handleSelectEpisode}
          />
        </Suspense>
      </>
    );
  }
  if (publicCreatorLoading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--cream, #f5ede0)', background: '#0c0808' }}>
        Loading profile…
      </div>
    );
  }
  if (publicCreatorMissing) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14, padding: 24, textAlign: 'center', color: 'var(--cream, #f5ede0)', background: '#0c0808' }}>
        <div style={{ fontSize: 22, fontWeight: 700 }}>We couldn’t find @{publicCreatorMissing}</div>
        <div style={{ opacity: 0.7, maxWidth: 360 }}>That creator hasn’t claimed this handle yet, or hasn’t set up their public profile.</div>
        <button
          className="btn-primary"
          onClick={() => {
            setPublicCreatorMissing(null);
            try { window.history.replaceState(null, '', '/'); } catch {}
          }}
        >
          Back to Epio
        </button>
      </div>
    );
  }

  // Standalone episode-preview page — the landing chat's locked result,
  // shown on its own page with the sign-up gate. Rendered outside
  // AppShell so it carries its own minimal nav. Once the visitor signs
  // up, the routing effect moves them into the app and this unmounts.
  if (view === 'episode-preview' && previewDoc) {
    return (
      <>
        <SessionGuard />
        <Suspense fallback={<Spinner center />}>
          <EpisodePreviewPage doc={previewDoc} />
        </Suspense>
      </>
    );
  }

  return (
    <>
      <SessionGuard />

      <CommandPalette
        open={showCommandPalette}
        onClose={() => setShowCommandPalette(false)}
        onNavigate={navigate}
        user={user}
      />

      <NotificationsPanel
        open={notifPanelOpen}
        onClose={() => setNotifPanelOpen(false)}
        user={user}
        profile={profile}
        onCountChange={refreshNotifCount}
      />

      <AppShell
        user={user}
        profile={profile}
        loading={loading}
        view={view}
        episodes={episodes}
        onNavigate={navigate}
        onOpenCommandPalette={() => setShowCommandPalette(true)}
        onOpenAuth={openAuth}
        onSignOut={handleSignOut}
        isDeepView={isDeepView}
        canGoBack={canGoBack}
        showExit={showExit}
        onBack={handleBack}
        onExitToHome={handleExitToHome}
        onStartBuilder={() => { setActiveEpisode(null); setBrief(INITIAL_BRIEF); setError(''); setView('builder'); }}
        onAskEpio={handleAskEpio}
        rightPanelTitle={
          view === 'builder' && user ? 'Workshop'
            : view === 'editor' ? 'Document'
            : view === 'shows' && user ? 'Activity'
            : undefined
        }
        rightPanel={
          view === 'shows' && user ? (
            <HomeActivityPanel profile={profile} onNavigate={navigate} />
          ) :
          view === 'builder' && user ? (
            <RecentEpisodesFeed
              episodes={episodes}
              shortForm={shortFormScripts}
              workspaces={workspaces}
              show={shows.find(s => s.id === activeShowId) || shows[0]}
              onSelectEpisode={handleSelectEpisode}
              onPlay={handlePlayEpisode}
              playingId={player.playing ? player.track?.episodeId : null}
              loadingId={audioLoadingId}
              onRemix={handleRemixEpisode}
              onRename={async (ep) => {
                const current = ep.topic || '';
                const next = window.prompt('Edit title', current);
                if (next == null) return;              // cancelled
                const trimmed = next.trim();
                if (!trimmed || trimmed === current) return;
                try {
                  await renameEpisode(user.id, ep.id, trimmed);
                  toast.success('Title updated');
                  loadEpisodes();
                } catch (e) {
                  toast.error(e?.message || 'Rename failed');
                }
              }}
              onPublish={async (ep) => {
                try {
                  const row = await markEpisodePublic(ep.id, user.id);
                  if (row?.slug) {
                    const url = `${window.location.origin}/e/${row.slug}`;
                    try { await navigator.clipboard.writeText(url); } catch {}
                    toast.success('Published — link copied');
                  } else {
                    toast.success('Published');
                  }
                  loadEpisodes();
                } catch (e) {
                  toast.error(e?.message || 'Publish failed');
                }
              }}
              onShare={async (ep) => {
                // Share copies the public URL if the episode is
                // public; otherwise it copies a clipboard message
                // prompting the user to publish first.
                const slug = ep?.slug || ep?.result?.slug;
                if (slug) {
                  const url = `${window.location.origin}/e/${slug}`;
                  try { await navigator.clipboard.writeText(url); } catch {}
                  toast.success('Link copied');
                } else {
                  toast('Tap Publish first to get a shareable link.');
                }
              }}
              onDownload={() => toast('Download coming soon.')}
              onManage={() => toast('Manage coming soon.')}
              onTrash={async (ep) => {
                if (!window.confirm(`Move "${ep.topic || 'this item'}" to Trash?`)) return;
                try {
                  await deleteEpisode(user.id, ep.id);
                  toast.success('Moved to Trash');
                  loadEpisodes();
                } catch (e) {
                  toast.error(e?.message || 'Delete failed');
                }
              }}
              remixBusyId={remixBusyId}
              highlightId={lastBuiltEpisodeId}
            />
          ) : view === 'editor' && activeEpisode ? (
            // Right rail on the editor view: Comments on top (close to
            // hand for engagement), then Similar / By-creator pinned
            // beneath with a max-height so it doesn't push the comments
            // out of view. Main column owns the document.
            (() => {
              const ep = activeEpisode;
              const activeShow = shows.find(s => s.id === (ep.show_id || activeShowId)) || shows[0];
              const creatorName = profile?.full_name?.split(' ')[0] || 'this creator';
              return (
                <>
                  <CommentsSection
                    episode={ep}
                    user={user}
                    profile={profile}
                    toast={toast}
                  />
                  <EpisodeRelatedPanel
                    episode={ep}
                    show={activeShow}
                    shows={shows}
                    creatorName={creatorName}
                    onSelectEpisode={handleSelectEpisode}
                    onSpinoff={(target) => handleRemixEpisode(target)}
                  />
                </>
              );
            })()
          ) : undefined
        }
        shows={shows}
        activeShowId={activeShowId}
        onSelectShow={(s) => { setActiveShowId(s.id); setView('builder'); }}
        onManageShows={() => navigate('shows-manager')}
        canCreateShow={summarizeShowUsage(effectivePlan(profile, isAdmin), shows.length).canCreate}
        onCreateShow={() => {
          setOpenCreateShowFlag(true);
          setView('shows');
        }}
        onOpenSearch={() => setShowCommandPalette(true)}
        onOpenNotifications={() => setNotifPanelOpen(true)}
        notificationCount={notifCount}
        inboxCount={inboxCount}
      >
        {/* Back button for internal drill-in pages — one shared affordance
            at the top of the stage (pages render below it). Returns to the
            previous view via the nav-history stack. */}
        {isInternalPage && (
          <button
            type="button"
            className="ws-page-back"
            onClick={handlePageBack}
            aria-label="Go back"
            title="Back"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
            <span>Back</span>
          </button>
        )}

        {/* Every view below is lazy-loaded; one Suspense boundary covers them
            all (only one renders at a time), so switching views shows a brief
            Spinner while that view's chunk downloads. */}
        <Suspense fallback={<Spinner center />}>
        {view === 'landing' && (
          <LandingV2 onEpisodePreview={handleEpisodePreview} />
        )}

        {/* Shows Dashboard — new workspace default */}
        {view === 'shows' && user && (
          <HomePage
            user={user}
            profile={profile}
            episodes={episodes}
            shows={shows}
            onNavigate={navigate}
            onPlayEpisode={handlePlayEpisode}
            onStartBuilder={handleAskEpio}
            activeShowId={activeShowId}
          />
        )}

        {/* Builder — the episode chat, hosted inside the app shell.
            Account-gated: a logged-out visitor is bounced to landing by
            the routing effect before this could render. */}
        {(view === 'builder' || view === 'brief') && user && (
          <CreateView
            user={user}
            profile={profile}
            seed={chatSeed}
            onSeedConsumed={() => setChatSeed(null)}
            onSaveUnsavedFromChat={handleSaveUnsavedFromChat}
            onChatBuildStart={({ pendingId, mode, topic }) => {
              // Drop an optimistic pending row into the right list
              // (episodes or shortFormScripts) the moment the chat
              // dispatches a build. The row carries a spinner +
              // disabled state via the `pending: true` flag; once
              // loadEpisodes() refreshes after the real save lands,
              // the pending placeholder is naturally replaced by the
              // saved row (matched by topic + recency, not pendingId,
              // since the server assigns a fresh uuid).
              const placeholder = {
                id:          pendingId,
                topic,
                format:      mode === 'soundbites' ? 'short_form' : 'solo',
                created_at:  new Date().toISOString(),
                pending:     true,
                result:      null,
              };
              setLastBuiltEpisodeId(pendingId);
              if (mode === 'soundbites') {
                setShortFormScripts(prev => [placeholder, ...prev]);
              } else {
                setEpisodes(prev => [placeholder, ...prev]);
              }
            }}
          />
        )}

        {/* Episode Editor — Suno-style replacement for ResultsWorkspace */}
        {view === 'editor' && user && activeEpisode && (() => {
          const activeShow = shows.find(s => s.id === (activeEpisode.show_id || activeShowId)) || shows[0];
          return (
            <EpisodeEditorPage
              user={user}
              profile={profile}
              episode={activeEpisode}
              activeShow={activeShow}
              onBack={() => navigate('shows')}
              onNavigate={navigate}
              onRemix={handleRemixEpisode}
              remixBusyId={remixBusyId}
              shows={shows}
              onSelectEpisode={handleSelectEpisode}
              toast={toast}
            />
          );
        })()}

        {/* Search — global discovery */}
        {view === 'search' && user && (
          <SearchPage
            user={user}
            profile={profile}
            myEpisodes={episodes}
            myShows={shows}
            onOpenEpisode={handleSelectEpisode}
            onOpenShow={(show) => {
              // If this is the user's own show, open their public profile.
              // Otherwise route to the show's builder / creator page later.
              if (show?.user_id === user?.id || shows.some(s => s.id === show?.id)) {
                navigate('profile');
                return;
              }
              setActiveShowId(show.id);
              setActiveEpisode(null);
              setError('');
              setView('builder');
            }}
            onOpenCreator={openCreatorInApp}
          />
        )}

        {/* Invite Friends */}
        {view === 'invite' && user && (
          <InvitePage user={user} profile={profile} />
        )}

        {/* Earn Credits */}
        {view === 'credits' && user && (
          <EarnCreditsPage user={user} profile={profile} onNavigate={navigate} />
        )}

        {/* Profile — view + inline edit (merged) */}
        {view === 'profile' && user && (
          <div className="page-scroll">
            <MyProfilePage
              user={user}
              profile={profile}
              onNavigate={navigate}
              onRefresh={refreshProfile}
              onCreateShow={() => navigate('shows-manager')}
              onOpenEpisode={handleSelectEpisode}
            />
          </div>
        )}

        {/* Mini-spec #4: Shows Manager + Show Editor */}
        {view === 'shows-manager' && user && (
          <div className="page-scroll">
            <ShowsManagerPage
              user={user}
              profile={profile}
              onNavigate={navigate}
              onOpenShow={(s) => navigate('show-editor', { showId: s.id })}
            />
          </div>
        )}
        {view === 'show-editor' && user && editingShowId && (
          <div className="page-scroll">
            <ShowEditorPage
              user={user}
              showId={editingShowId}
              onNavigate={navigate}
              onBackToManager={() => { setEditingShowId(null); navigate('shows-manager'); }}
            />
          </div>
        )}
        {view === 'show-editor' && user && !editingShowId && (
          <div className="page-scroll">
            {(() => { setTimeout(() => { setView('shows-manager'); }, 0); return null; })()}
          </div>
        )}

        {/* Mini-spec #9: public hook leaderboard. Auth-optional —
            anyone can land here and browse, signed-in users get
            "Build an episode" CTAs. */}
        {view === 'leaderboard' && (
          <div className="page-scroll">
            <LeaderboardPage onNavigate={navigate} />
          </div>
        )}

        {/* Legacy guest-profile / guest-profile-edit hashes now route to
            the single consolidated Profile page. MyProfilePage covers
            both the view-as-visitor and the owner-edit surfaces via its
            preview toggle. */}
        {(view === 'guest-profile' || view === 'guest-profile-edit') && user && (
          <div className="page-scroll">
            <MyProfilePage
              user={user}
              profile={profile}
              onNavigate={navigate}
              onRefresh={refreshProfile}
              onCreateShow={() => navigate('shows-manager')}
              onOpenEpisode={handleSelectEpisode}
            />
          </div>
        )}

        {/* Account — plan, billing, preferences hub */}
        {view === 'account' && user && (
          <div className="page-scroll"><AccountPage user={user} profile={profile} onNavigate={navigate} /></div>
        )}

        {/* Inbox — host-guest pitches (becomes the DM hub in PR-B; today
            it's the canonical pitch surface). */}
        {view === 'inbox' && user && (
          <div className="page-scroll"><InboxPage onNavigate={navigate} /></div>
        )}

        {/* Collabs — dedicated management ecosystem for guesting collabs.
            Five-tab pipeline (Received / Sent / Booked / Completed /
            Reviews) lives in its own page so it doesn't share state or
            UX with the Messages inbox. */}
        {view === 'collabs' && user && (
          <div className="page-scroll"><CollabsPage onNavigate={navigate} /></div>
        )}

        {/* Matches — ranked podcasters by compatibility */}
        {view === 'matches' && user && (
          <div className="page-scroll"><MatchesPage onNavigate={navigate} profile={profile} /></div>
        )}

        {/* Dashboard — legacy (kept accessible via #dashboard) */}
        {view === 'dashboard' && user && (
          <div className="page-scroll">
            <DashboardView
              profile={profile}
              episodes={episodes}
              onStartBuilder={() => setView('builder')}
              onStartWithTopic={handleStartWithTopic}
              onSelectEpisode={handleSelectEpisode}
              onNavigate={navigate}
              onBuildIdea={(idea) => handleStartWithTopic(idea.topic_seed || idea.title, 'build')}
              onBuildFromTrend={handleBuildFromTrend}
              onImproveEpisode={handleImproveEpisode}
              onPackageEpisode={handlePackageEpisode}
            />
          </div>
        )}

        {/* Thinking — fills container */}
        {view === 'thinking' && (
          <ThinkingView
            topic={brief.topic}
            steps={THINKING_STEPS}
            currentIndex={thinkingIndex}
            done={!isBuilding}
          />
        )}

        {/* Results — scrollable */}
        {view === 'results' && activeEpisode && (
          <div className="page-scroll">
            <ResultsWorkspace
              episode={activeEpisode}
              onStartNew={startNewEpisode}
              onPodcastAction={handlePodcastAction}
              onRequireAccount={() => openAuth('signup')}
              isLoggedIn={!!user}
              onNavigate={navigate}
            />
          </div>
        )}

        {/* Library — cross-show catalog with filters + bulk actions */}
        {view === 'episodes' && user && (
          <LibraryPage
            user={user}
            episodes={episodes}
            shows={shows}
            onOpenEpisode={handleSelectEpisode}
            onEpisodesChanged={loadEpisodes}
            onNavigate={navigate}
          />
        )}

        {/* Analytics — scrollable */}
        {view === 'analytics' && user && (
          <div className="page-scroll">
            <AnalyticsPage episodes={episodes} profile={profile} onNavigate={navigate} />
          </div>
        )}

        {/* Marketplace (legacy alias) — renders the new PartnersPage */}
        {view === 'marketplace' && (
          <div className="page-scroll">
            <PartnersPage onNavigate={navigate} />
          </div>
        )}

        {/* Partners — the renamed marketplace, "Coming soon" */}
        {view === 'partners' && (
          <div className="page-scroll">
            <PartnersPage onNavigate={navigate} />
          </div>
        )}

        {/* Pricing — plans & checkout entry point */}
        {view === 'pricing' && (
          <div className="page-scroll">
            <PricingPage onNavigate={navigate} />
          </div>
        )}

        {/* Billing — the new balance-based billing settings page */}
        {view === 'billing' && user && (
          <div className="page-scroll">
            <BillingSettingsPage onNavigate={navigate} />
          </div>
        )}

        {/* Subscription — closed-beta plans hub. Wraps BillingSettingsPage
            with a beta gate when paidPlansEnabled is false. */}
        {view === 'subscription' && user && (
          <div className="page-scroll">
            <SubscriptionPage onNavigate={navigate} user={user} profile={profile} />
          </div>
        )}

        {/* Settings — scrollable */}
        {view === 'settings' && user && (
          <div className="page-scroll">
            <SettingsPage user={user} profile={profile} onRefresh={refreshProfile} onNavigate={navigate} />
          </div>
        )}

        {/* Help — scrollable */}
        {view === 'help' && user && (
          <div className="page-scroll"><HelpPage onNavigate={navigate} /></div>
        )}

        {/* Support — ticketing system (AI-assisted) */}
        {view === 'support' && user && (
          <div className="page-scroll"><SupportPage /></div>
        )}

        {/* Workspaces — topic folders for organizing episodes */}
        {view === 'workspaces' && user && (
          <div className="page-scroll"><WorkspacesPage onNavigate={navigate} /></div>
        )}

        {/* Clip upload (legacy) — kept for deep links; new entry is the
            UploadClipModal triggered from within ClipsPage. */}
        {view === 'clip-upload' && user && (
          <div className="page-scroll">
            <ClipUploadPage
              shows={shows}
              activeShowId={activeShowId}
              onNavigate={navigate}
            />
          </div>
        )}

        {/* Clips — vertical feed, responsive (mobile full-viewport feed,
            desktop three-panel layout with transcript/comments/insights). */}
        {view === 'clips' && user && (
          <ClipsPage
            shows={shows}
            activeShowId={activeShowId}
            onNavigate={navigate}
          />
        )}

        {/* Labs — scrollable */}
        {view === 'labs' && user && (
          <div className="page-scroll"><LabsPage /></div>
        )}

        {/* Short-form lives inside the chat on /create (Short-form tab)
            now; the standalone ShortFormPage was removed. Legacy
            /short-form URLs redirect to /create via routing.js's
            LEGACY_PATH_REDIRECTS so old bookmarks still work. */}

        {/* Arcs feature landing page — reached from the home FeaturedBanner
            "Learn more" CTA. Higher-tier feature; the page itself
            handles the upgrade gate. */}
        {view === 'arcs' && (
          <div className="page-scroll"><ArcsLandingPage profile={profile} onNavigate={navigate} /></div>
        )}

        {/* Explore — scrollable */}
        {view === 'explore' && user && (
          <div className="page-scroll"><ExplorePage profile={profile} onOpenCreator={openCreatorInApp} /></div>
        )}

        {/* Admin — scrollable */}
        {view === 'admin' && user && (
          <div className="page-scroll"><AdminPage /></div>
        )}
        </Suspense>
      </AppShell>

      {/* Onboarding takeover — full-screen, sits above AppShell + AuthModal.
          Renders only when the auth-success effect detected an
          unfinished onboarding row. self-managed; we only listen for
          onComplete. */}
      {showOnboarding && user && (
        <OnboardingFlow
          entryPoint={onboardingEntry}
          onComplete={handleOnboardingComplete}
        />
      )}

      {showAuth && (
        <AuthModal
          mode={authMode}
          onClose={() => setShowAuth(false)}
          onSuccess={() => setShowAuth(false)}
        />
      )}

      {/* Soft low-credit banner — only renders when balance ≤ 50 & > 0.
          PR-A: every Upgrade CTA routes to the canonical 'subscription'
          view. SubscriptionPage internally renders the closed-beta gate
          or the live BillingSettingsPage based on featureFlags.
          paidPlansEnabled — single source of truth for the gate. */}
      {user && (
        <LowCreditBanner
          onBuyTopup={() => navigate('billing')}
          onUpgrade={() => navigate('subscription')}
        />
      )}
    </>
  );
}
